// Pointer lock that asks for raw input (unadjustedMovement) first. Raw input is only supported on
// some OS/browser combinations. A retry has to come from a real click, so on failure we remember to
// skip raw input and ask for one more click instead of retrying from a promise callback.
//
// `locked` means "in the game" (input goes to the game, the menu is hidden). Usually that is a real
// pointer lock. Two modes play without one: touch screens (no mouse), and the level builder's
// cursor mode, where the mouse is free to click pieces and panels and is only captured while the
// right button is held to look around (onLook).

import { getFlag, setFlag } from '../storage/settings';

const NO_RAW_KEY = 'bhop.noraw';

export class PointerLock {
  locked = false;
  /** Whether the current lock uses raw input. */
  raw = false;
  /** `refused`: a lock was asked for and the browser said no (the game wasn't left on purpose). */
  onChange: (locked: boolean, refused?: boolean) => void = () => {};
  /** Show a message to the player ('' clears it). */
  onMessage: (msg: string) => void = () => {};
  /** Cursor mode: the mouse was captured (true) or freed (false) for looking around. */
  onLook: (looking: boolean) => void = () => {};

  /**
   * Phones and tablets have no mouse to capture: "locking" just means playing (the touch controls
   * show), and release() goes back to the menu.
   */
  touchMode = false;
  /** The level builder: in the game with a free cursor; the right mouse button captures it to look. */
  private cursorMode = false;
  /** A real pointer lock is held. */
  private real = false;

  private rawUnsupported = getFlag(NO_RAW_KEY);
  private attempt = 0;
  private handled = -1;
  private lastUnlock = 0;

  constructor(private readonly target: HTMLElement) {
    document.addEventListener('pointerlockchange', () => {
      this.real = document.pointerLockElement === target;
      if (this.cursorMode) {
        // Looking around in the builder: still in the game either way.
        this.onLook(this.real);
        return;
      }
      if (this.real === this.locked) return;
      this.locked = this.real;
      if (!this.locked) this.lastUnlock = performance.now();
      else {
        this.raw = !this.rawUnsupported;
        this.onMessage('');
      }
      this.onChange(this.locked);
    });
    document.addEventListener('pointerlockerror', () => {
      if (this.cursorMode) return;
      setTimeout(() => this.failed(null), 0);
    });
  }

  /** In cursor mode (the builder)? */
  get cursor() {
    return this.cursorMode;
  }

  /** Mouse captured for looking around in cursor mode. */
  get looking() {
    return this.cursorMode && this.real;
  }

  /**
   * Switch cursor mode. Turning it on keeps you in the game (a held pointer lock is let go, the
   * cursor shows). Turning it off leaves "in the game" without a lock: call request() next, from a
   * click or key press, to capture the mouse for playing.
   */
  setCursorMode(on: boolean) {
    if (on === this.cursorMode) return;
    this.cursorMode = on;
    if (on) {
      if (this.real) document.exitPointerLock();
    } else if (this.locked && !this.real) {
      this.locked = false;
    }
  }

  /** Cursor mode: capture the mouse to look around (from a mouse-down). */
  startLook() {
    if (!this.cursorMode || this.real) return;
    try {
      const r = this.target.requestPointerLock() as Promise<void> | void;
      if (r && typeof r.catch === 'function') r.catch(() => {});
    } catch {
      // No lock: looking just doesn't start.
    }
  }

  stopLook() {
    if (this.cursorMode && this.real) document.exitPointerLock();
  }

  release() {
    if (this.touchMode || this.cursorMode) {
      if (this.real) document.exitPointerLock();
      if (!this.locked) return;
      this.locked = false;
      this.lastUnlock = performance.now();
      this.onChange(false);
    } else document.exitPointerLock();
  }

  /** Must be called from inside a real click handler. */
  request() {
    this.onMessage('');
    if (this.touchMode || this.cursorMode) {
      if (this.touchMode && window.innerHeight > window.innerWidth) {
        this.onMessage('Turn your phone sideways to play: the game only plays in landscape.');
        return;
      }
      if (this.locked) return;
      this.locked = true;
      this.onChange(true);
      return;
    }
    this.attempt++;
    let r: Promise<void> | void;
    try {
      r = this.rawUnsupported
        ? this.target.requestPointerLock()
        : this.target.requestPointerLock({ unadjustedMovement: true });
    } catch (e) {
      this.failed(e as Error);
      return;
    }
    if (r && typeof r.catch === 'function') r.catch((e: Error) => this.failed(e));
  }

  private failed(err: Error | null) {
    if (this.handled === this.attempt) return;
    this.handled = this.attempt;
    // Left cursor mode for a play test but the lock was refused: show the menu (with the message).
    if (!this.locked) this.onChange(false, true);
    if (!this.rawUnsupported && (!err || err.name === 'NotSupportedError' || err.name === 'TypeError')) {
      this.rawUnsupported = true;
      setFlag(NO_RAW_KEY);
      this.onMessage(
        'Raw mouse input isn’t supported in this browser, so the game will use normal mouse input. Click Play again.',
      );
      return;
    }
    if (performance.now() - this.lastUnlock < 1500) {
      this.onMessage('The browser needs a moment after Esc. Click Play again.');
    } else {
      this.onMessage(
        'This view blocks mouse capture. Open the game in its own browser tab and click Play there.',
      );
    }
  }
}
